import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  VStack,
  Input,
  Select,
  Button,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Tag,
  Avatar,
  Grid,
  GridItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  Container,
  HStack,
  Stack,
  useColorModeValue,
} from "@chakra-ui/react";
import "./ProfileSetting.scss";
import { useDispatch, useSelector } from "react-redux";
import { getAllSectors } from "../../actions/sector/sectorActions";
import {
  getUserDetail,
  updateUserProfile,
} from "../../actions/user/userActions";
import { SmallAddIcon } from "@chakra-ui/icons";
import { TYPES_OF_COMPETATIVE_METRICS } from "../../constants/metrics";
import { toast } from "react-toastify";

// Languages
const languages = ["English", "Marathi", "Hindi", "Sanskrit", "Kannada"];

const ProfileSetting = () => {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [yearsOfExperience, setYearsOfExperience] = useState(0);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [currentSelectedIndustries, setCurrentSelectedIndustries] = useState(
    {}
  );
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [metricType, setMetricType] = useState("Topline");
  const [subMetricType, setSubMetricType] = useState("");
  const [competitiveMenuObjects, setCompetitiveMenuObjects] = useState({});
  const [competitiveMenuLabel, setCompetitiveMenuLabel] = useState({});
  const [caseStudies, setCaseStudies] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    highestEducation: "",
    instituteCompletion: "",
  });

  const [formUseCaseData, setFormUseCaseData] = useState({
    actionTaken: "",
    resultsDescription: "",
    referenceEmail1: "",
    referenceEmail2: "",
  });

  const { loading: sectorsLoading, data: sectorsData } = useSelector(
    (state) => state.sectorsReducer
  );

  const { loading: userLoading, data: userData } = useSelector(
    (state) => state.userDetailReducer
  );

  const { loading: userProfileLoading, data: userProfileData } = useSelector(
    (state) => state.userProfileReducer
  );

  useEffect(() => {
    dispatch(getAllSectors());
  }, []);

  useEffect(() => {
    dispatch(getUserDetail());
  }, []);

  useEffect(() => {
    if (!userLoading && userData && sectorsData) {
      setFormData(userData?.qualification[0] || {});
      setPreviewUrl(userData?.profileImage || null);
      setYearsOfExperience(userData?.yearsOfExperience);
      setSelectedLanguages(userData?.languages);
      setSelectedIndustries(
        sectorsData
          .filter((sector) => (userData?.sectorIds).includes(sector.id))
          .map((data) => ({
            id: data.id,
            sectorName: data.sectorName,
          }))
      );
    }
  }, [userLoading, userData]);

  // Handle language selection
  const toggleLanguage = (language) => {
    if (selectedLanguages.includes(language)) {
      setSelectedLanguages(
        selectedLanguages.filter((lang) => lang !== language)
      );
    } else {
      setSelectedLanguages([...selectedLanguages, language]);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaseStudyChange = (e) => {
    setFormUseCaseData({ ...formUseCaseData, [e.target.name]: e.target.value });
  };

  // Handle industry selection
  const toggleIndustry = (sector) => {
    const isSelected = selectedIndustries.find((item) => item.id === sector.id);
    if (isSelected) {
      setCurrentSelectedIndustries({});
      setSelectedIndustries(
        selectedIndustries.filter((ind) => ind.id !== sector.id)
      );
    } else {
      setCurrentSelectedIndustries(sector);
      setSelectedIndustries((prevItems) => [...prevItems, sector]);
    }
  };

  const handleAddCaseStudy = () => {
    // This would typically open a modal to add a case study
    setFormUseCaseData({
      actionTaken: "",
      resultsDescription: "",
      referenceEmail1: "",
      referenceEmail2: "",
    });
    setMetricType("Topline");
    setSubMetricType("");
    onOpen();
  };

  const handleAddUseCases = () => {
    if (
      metricType === ""
    ) {
      toast.error("Please select Metric Type", {
        position: "bottom-center",
        toastId: "pswd-mismatch",
      });
    }else if (subMetricType === "") {
      toast.error(`Please select ${metricType} Type`, {
        position: "bottom-center",
        toastId: "pswd-mismatch",
      });
    } else if (formUseCaseData.actionTaken === "") {
      toast.error("Please enter Action Taken", {
        position: "bottom-center",
        toastId: "pswd-mismatch",
      });
    } else if (formUseCaseData.resultsDescription === "") {
      toast.error("Please enter Result Descriptions", {
        position: "bottom-center",
        toastId: "pswd-mismatch",
      });
    } else if (
      formUseCaseData.referenceEmail1 !== "" &&
      !emailRegex.test(formUseCaseData.referenceEmail1)
    ) {
      toast.error("Please enter valid email reference 1", {
        position: "bottom-center",
        toastId: "pswd-mismatch",
      });
    } else if (
      formUseCaseData.referenceEmail2 !== "" &&
      !emailRegex.test(formUseCaseData.referenceEmail2)
    ) {
      toast.error("Please enter valid email reference 2", {
        position: "bottom-center",
        toastId: "pswd-mismatch",
      });
    } else {
      setCaseStudies([
        ...caseStudies,
        {
          ...formUseCaseData,
          sectorId: currentSelectedIndustries.id,
          metricType: metricType,
          subMetricType: subMetricType,
        },
      ]);
      onClose();
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setImageFile(file);
    }
  };

  const handleSubmit = () => {
    console.log(
      formData,
      yearsOfExperience,
      selectedLanguages,
      selectedIndustries,
      caseStudies
    );
    const data = new FormData();
    data.append("yearsOfExperience", yearsOfExperience);
    data.append("qualification", JSON.stringify([formData]));
    data.append("languages", JSON.stringify(selectedLanguages));
    data.append(
      "industries",
      JSON.stringify(
        selectedIndustries.map((selectedIndustry) => selectedIndustry.id)
      )
    );
    data.append("caseStudies", JSON.stringify(caseStudies));
    if (imageFile) {
      data.append("file", imageFile);
    }
    dispatch(updateUserProfile(data));
  };

  useEffect(() => {
    const result = {};
    const labelMap = {};
    TYPES_OF_COMPETATIVE_METRICS.forEach((category) => {
      const categoryName = category.label;
      result[categoryName] = [];

      category.value.forEach((subCategory) => {
        subCategory.value.forEach((metric) => {
          result[categoryName].push(metric.value);
          labelMap[metric.value] = metric.label;
        });
      });
    });
    setCompetitiveMenuObjects(result);
    setCompetitiveMenuLabel(labelMap);
  }, []);
  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={6}
      mx="auto"
      boxShadow="md"
      bg="white"
      m={10}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md">Profile Settings</Heading>
      </Flex>
      <Grid
        templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
        gap={8}
      >
        <GridItem colSpan={1}>
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            {/* Name */}
            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }}>Name</FormLabel>
              <Input
                value={userData?.full_name}
                size={{ base: "sm", md: "md" }}
                disabled={"true"}
              />
            </FormControl>

            {/* Education */}
            <FormControl>
              <FormLabel>Highest Education</FormLabel>
              <Input
                name="highestEducation"
                placeholder="Highest Education"
                onChange={handleChange}
                value={formData?.highestEducation}
              />
            </FormControl>

            {/* Institute */}
            <FormControl>
              <FormLabel>Institute Of Completion</FormLabel>
              <Input
                name="instituteCompletion"
                placeholder="Enter institute name"
                onChange={handleChange}
                value={formData?.instituteCompletion}
              />
            </FormControl>

            {/* Experience */}
            <FormControl>
              <FormLabel>Years of Experience</FormLabel>
              <Flex>
                <NumberInput
                  name="yearsOfExperience"
                  value={yearsOfExperience || 0}
                  onChange={(value) => setYearsOfExperience(Number(value))}
                  min={0}
                  max={50}
                  width="100px"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Flex>
            </FormControl>

            {/* Languages */}
            <FormControl>
              <FormLabel>Select languages you are comfortable with</FormLabel>
              <Flex gap={2} flexWrap="wrap">
                {languages.map((language) => (
                  <Tag
                    key={language}
                    size="md"
                    borderRadius="full"
                    variant={
                      selectedLanguages.includes(language) ? "solid" : "outline"
                    }
                    colorScheme={
                      selectedLanguages.includes(language) ? "gray" : "gray"
                    }
                    bg={
                      selectedLanguages.includes(language)
                        ? "gray.700"
                        : "white"
                    }
                    color={
                      selectedLanguages.includes(language) ? "white" : "black"
                    }
                    cursor="pointer"
                    onClick={() => toggleLanguage(language)}
                    px={4}
                    py={2}
                    my={1}
                  >
                    {language}
                  </Tag>
                ))}
              </Flex>
            </FormControl>

            {/* Avatar */}
            <Box mt={4}>
              <Input
                type="file"
                id="imageUpload"
                style={{ display: "none" }}
                onChange={handleImageUpload}
                accept="image/*"
              />
              <label htmlFor="imageUpload">
                <Box as="span" cursor="pointer">
                  <Avatar size="lg" src={previewUrl} alt="Preview" />
                </Box>
              </label>
            </Box>
          </VStack>
        </GridItem>

        <GridItem colSpan={1}>
          <VStack spacing={6} align="stretch">
            {/* Industry Expertise */}
            <FormControl>
              <FormLabel>Industries Of Expertise</FormLabel>
              <Grid
                templateColumns={{
                  base: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                }}
                gap={2}
                height="300px"
                overflowY="auto"
              >
                {!sectorsLoading &&
                  sectorsData.map((sector) => {
                    const isSelected = selectedIndustries.find(
                      (item) => item.id === sector.id
                    );

                    return (
                      <GridItem key={sector._id}>
                        <Flex
                          p={2}
                          borderWidth="1px"
                          borderRadius="md"
                          alignItems="center"
                          cursor="pointer"
                          onClick={() =>
                            toggleIndustry({
                              sectorName: sector.sectorName,
                              id: sector.id,
                            })
                          }
                          bg={isSelected ? "gray.100" : "white"}
                          _hover={{ bg: "gray.50" }}
                        >
                          <Text
                            fontSize="sm"
                            noOfLines={1}
                            title={sector.sectorName}
                          >
                            {sector.sectorName}
                          </Text>
                        </Flex>
                      </GridItem>
                    );
                  })}
              </Grid>
            </FormControl>

            {/* Food And Kindred Products Section */}
            {currentSelectedIndustries?.id && (
              <Box mt={6}>
                <Heading size="sm" mb={4}>
                  {currentSelectedIndustries.sectorName}
                </Heading>

                <Flex justify="start" align="center">
                  <Text mr={5}>Add Case Studies</Text>
                  <IconButton
                    icon={<SmallAddIcon />}
                    aria-label="Add case study"
                    variant="outline"
                    onClick={handleAddCaseStudy}
                    size="lg"
                    isRound
                    bg="white"
                  />
                </Flex>
              </Box>
            )}
          </VStack>
        </GridItem>
      </Grid>
      <Flex justify="center" mt={10}>
        <Button
          colorScheme="blackAlpha"
          bg="black"
          size="md"
          px={10}
          _hover={{ bg: "gray.800" }}
          onClick={handleSubmit}
        >
          {userProfileLoading ? "Loading..." : "Submit"}
        </Button>
      </Flex>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: "full", md: "xl", lg: "2xl" }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{currentSelectedIndustries.sectorName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              <FormControl isRequired>
                <FormLabel fontWeight="medium">Metric Type Improved</FormLabel>
                <HStack spacing={2} mt={2}>
                  {competitiveMenuObjects &&
                    Object.keys(competitiveMenuObjects).map((type) => (
                      <Button
                        key={type}
                        size="md"
                        variant={metricType === type ? "solid" : "outline"}
                        bg={metricType === type ? "gray.600" : "transparent"}
                        color={metricType === type ? "white" : "inherit"}
                        _hover={{
                          bg: metricType === type ? "gray.700" : "gray.100",
                        }}
                        onClick={() => setMetricType(type)}
                      >
                        {type}
                      </Button>
                    ))}
                </HStack>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="medium">
                  Choose {metricType} Improved
                </FormLabel>
                <HStack spacing={2} flexWrap="wrap" mt={2}>
                  {competitiveMenuObjects[metricType] &&
                    competitiveMenuObjects[metricType].map((metric) => (
                      <Button
                        key={metric}
                        size="md"
                        mb={2}
                        variant={subMetricType === metric ? "solid" : "outline"}
                        bg={
                          subMetricType === metric ? "gray.600" : "transparent"
                        }
                        color={subMetricType === metric ? "white" : "inherit"}
                        _hover={{
                          bg:
                            subMetricType === metric ? "gray.700" : "gray.100",
                        }}
                        onClick={() => setSubMetricType(metric)}
                      >
                        {competitiveMenuLabel[metric]}
                      </Button>
                    ))}
                </HStack>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="medium">Action Taken</FormLabel>
                <Textarea
                  placeholder="Describe the actions taken..."
                  size="md"
                  rows={4}
                  resize="vertical"
                  name="actionTaken"
                  onChange={handleCaseStudyChange}
                  value={formUseCaseData?.actionTaken}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="medium">Results</FormLabel>
                <Textarea
                  placeholder="Describe the results..."
                  size="md"
                  rows={4}
                  resize="vertical"
                  name="resultsDescription"
                  onChange={handleCaseStudyChange}
                  value={formUseCaseData?.resultsDescription}
                />
              </FormControl>

              <Text
                fontSize="sm"
                color="gray.500"
                mt={2}
                display="flex"
                alignItems="center"
              >
                <Box as="span" mr={2} fontSize="lg">
                  •
                </Box>
                Reference checks help us to provide you with accurate insights
              </Text>

              <Stack
                direction={{ base: "column", md: "row" }}
                spacing={4}
                mt={4}
              >
                <FormControl flex="1">
                  <Input
                    name="referenceEmail1"
                    placeholder="Client Email 1"
                    onChange={handleCaseStudyChange}
                    value={formUseCaseData?.referenceEmail1}
                  />
                </FormControl>
                <FormControl flex="1">
                  <Input
                    name="referenceEmail2"
                    placeholder="Client Email 2"
                    onChange={handleCaseStudyChange}
                    value={formUseCaseData?.referenceEmail2}
                  />
                </FormControl>
              </Stack>

              <Box display="flex" justifyContent="center" mt={6}>
                <Button
                  colorScheme="gray"
                  bg="gray.600"
                  color="white"
                  size="md"
                  _hover={{ bg: "gray.700" }}
                  onClick={handleAddUseCases}
                >
                  Add Industry Experience
                </Button>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProfileSetting;